/*
 * Publish node field selection + renames (per workflow).
 *
 * The Publish node owns WHICH fields go to a Library destination and UNDER WHAT
 * NAME. Stored on the node config:
 *
 *   excluded_fields: string[]              — denylist of Capture keys not sent
 *   field_mapping:   { captureKey: target } — renames only; identity dropped
 *
 * Legacy nodes carry included_fields (an allowlist). It keeps working until the
 * node is saved in this UI, which converts it: excluded = picker keys − included.
 * A node with neither keeps the service fallback (sync_to_library) and shows it
 * as the default ticks.
 *
 * Pure functions only — the panel and its tests share them.
 */

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

/** Catalog rows eligible as picker rows: active, scope both|capture. */
const isPickerCatalogField = (f) =>
  f && f.field_key && f.is_active !== false && (f.applies_to === 'both' || f.applies_to === 'capture' || f.applies_to == null);

/** Catalog rows eligible as rename targets: active, scope both|library. */
const isTargetCatalogField = (f) =>
  f && f.field_key && f.is_active !== false && (f.applies_to === 'both' || f.applies_to === 'library' || f.applies_to == null);

function dedupeByKey(fields, keep) {
  const byKey = new Map();
  for (const f of fields || []) {
    if (!keep(f) || byKey.has(f.field_key)) continue;
    byKey.set(f.field_key, f);
  }
  return byKey;
}

/**
 * Picker rows = active catalog fields (scope both/capture) UNION the tenant's
 * distinct extracted keys. An extracted key the catalog does not list (or lists
 * only as inactive) is still a row: it is sent today, so it must be visible to
 * be unticked. Rows are { field_key, label, category, sync_to_library, inCatalog }.
 */
export function buildPickerRows(catalogFields, extractedKeys) {
  const active = dedupeByKey(catalogFields, isPickerCatalogField);
  const anyCatalog = dedupeByKey(catalogFields, (f) => f && f.field_key);
  const rows = [];
  for (const f of active.values()) {
    rows.push({
      field_key: f.field_key,
      label: f.label || f.field_key,
      category: f.category || 'custom',
      sync_to_library: f.sync_to_library !== false,
      inCatalog: true,
    });
  }
  for (const key of extractedKeys || []) {
    if (!key || active.has(key)) continue;
    const meta = anyCatalog.get(key);
    rows.push({
      field_key: key,
      label: meta?.label || key,
      category: meta?.category || 'custom',
      // Not an active catalog field → nothing says "don't sync" → ticked.
      sync_to_library: true,
      inCatalog: false,
    });
    active.set(key, null);
  }
  return rows;
}

/** Rename targets: active Library-visible catalog fields (scope both/library). */
export function buildTargetOptions(catalogFields) {
  return [...dedupeByKey(catalogFields, isTargetCatalogField).values()]
    .map((f) => ({ field_key: f.field_key, label: f.label || f.field_key }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Which selection the node carries: 'excluded' | 'legacy_included' | 'default'. */
export function selectionMode(config) {
  if (Array.isArray(config?.excluded_fields)) return 'excluded';
  if (Array.isArray(config?.included_fields)) return 'legacy_included';
  return 'default';
}

/** Is this row ticked (sent) under the node's current config? */
export function isRowSent(config, row) {
  switch (selectionMode(config)) {
    case 'excluded':
      return !config.excluded_fields.includes(row.field_key);
    case 'legacy_included':
      return config.included_fields.includes(row.field_key);
    default:
      return row.sync_to_library !== false;
  }
}

/** The denylist the rows currently show, whatever the stored mode. */
function currentExcluded(config, rows) {
  return rows.filter((r) => !isRowSent(config, r)).map((r) => r.field_key);
}

/**
 * Toggle Send for one row. Always writes excluded_fields and removes
 * included_fields — the first edit is also the conversion.
 */
export function toggleSend(config, rows, fieldKey) {
  const excluded = new Set(currentExcluded(config, rows));
  // Keep stored exclusions for keys not currently listed (never silently re-send them).
  if (selectionMode(config) === 'excluded') {
    for (const k of config.excluded_fields) excluded.add(k);
  }
  if (excluded.has(fieldKey)) excluded.delete(fieldKey);
  else excluded.add(fieldKey);
  const next = { ...config, excluded_fields: [...excluded].sort() };
  delete next.included_fields;
  return next;
}

/** Drop identity and empty entries — what is actually saved. */
export function normalizeMapping(mapping) {
  const out = {};
  for (const [source, target] of Object.entries(mapping || {})) {
    if (typeof target !== 'string' || target === '' || target === source) continue;
    out[source] = target;
  }
  return out;
}

/** Set (or clear, with '' / same name) the rename for one row. */
export function setRename(config, fieldKey, target) {
  const mapping = { ...(config?.field_mapping || {}) };
  if (!target || target === fieldKey) delete mapping[fieldKey];
  else mapping[fieldKey] = target;
  const next = { ...config, field_mapping: normalizeMapping(mapping) };
  if (Object.keys(next.field_mapping).length === 0) delete next.field_mapping;
  return next;
}

/** Targets used by more than one source (the save would refuse these). */
export function duplicateTargets(mapping) {
  const seen = new Map();
  for (const [source, target] of Object.entries(normalizeMapping(mapping))) {
    seen.set(target, [...(seen.get(target) || []), source]);
  }
  return [...seen.entries()].filter(([, sources]) => sources.length > 1).map(([t]) => t);
}

/**
 * Applied when the node is saved in this UI ("Update Node").
 *   - legacy included_fields → excluded_fields = picker keys − included;
 *     included_fields removed. Needs the picker rows: with none loaded the
 *     legacy allowlist is left exactly as it was.
 *   - field_mapping normalised (identity dropped; removed when empty).
 */
export function prepareConfigForSave(config, rows) {
  let next = { ...(config || {}) };
  if (selectionMode(next) === 'legacy_included' && Array.isArray(rows) && rows.length > 0) {
    const included = new Set(next.included_fields);
    next.excluded_fields = rows.map((r) => r.field_key).filter((k) => !included.has(k)).sort();
    delete next.included_fields;
  }
  if (hasOwn(next, 'field_mapping')) {
    const mapping = normalizeMapping(next.field_mapping);
    if (Object.keys(mapping).length === 0) delete next.field_mapping;
    else next.field_mapping = mapping;
  }
  return next;
}

/**
 * Client mirror of capture-app services/setup/fieldKey.ts slugify: lowercase →
 * strip everything not [a-z0-9\s] → trim → collapse whitespace runs to "_".
 * ASCII-only on purpose (same as the server), so a label with no Latin letters
 * or digits derives an EMPTY key — the modal blocks it.
 */
export function deriveFieldKey(label) {
  return String(label ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_');
}
